// Move the HUD around: chat, the minimap column, party frames and the quest tracker. "Edit layout" (Settings →
// Interface, or the button in the bar it shows) makes them draggable; the chat can also be made wider or narrower.
// Positions are saved on this device as a share of the screen, so they survive a window resize; Reset puts
// everything back.
const KEY = 'tt.layout.v1';
const $ = (id: string) => document.getElementById(id)!;

interface Spot { x: number; y: number; w?: number }   // left/top as a share of the screen; chat width in px
const UNITS: { id: string; label: string; resize?: boolean }[] = [
  { id: 'chat', label: 'Chat', resize: true },
  { id: 'hudRight', label: 'Minimap' },
  { id: 'party', label: 'Party' },
  { id: 'tracker', label: 'Quest' },
];

export class Layout {
  private spots: Record<string, Spot> = {};
  editing = false;
  onEdit: (on: boolean) => void = () => {};

  constructor() {
    try { this.spots = JSON.parse(localStorage.getItem(KEY) || '{}'); } catch { this.spots = {}; }
    $('layoutDone').addEventListener('click', () => this.edit(false));
    $('layoutReset').addEventListener('click', () => this.reset());
    addEventListener('resize', () => this.apply());
    addEventListener('keydown', e => { if (this.editing && e.code === 'Escape') this.edit(false); });
    for (const u of UNITS) this.draggable(u.id);
    this.apply();
  }

  /** Put every moved element where it was left (clamped onto the screen). */
  apply(): void {
    for (const u of UNITS) {
      const el = $(u.id), s = this.spots[u.id];
      if (!s) { el.style.left = el.style.top = el.style.right = el.style.bottom = el.style.width = el.style.transform = ''; el.classList.remove('moved'); continue; }
      el.classList.add('moved');
      if (s.w) el.style.width = `${Math.max(220, Math.min(innerWidth - 16, s.w))}px`;
      const r = el.getBoundingClientRect(), w = r.width || 180, h = r.height || 40;
      el.style.right = el.style.bottom = 'auto'; el.style.transform = 'none';
      el.style.left = `${Math.round(Math.max(4, Math.min(innerWidth - w - 4, s.x * innerWidth)))}px`;
      el.style.top = `${Math.round(Math.max(4, Math.min(innerHeight - Math.min(h, 60) - 4, s.y * innerHeight)))}px`;
    }
  }

  edit(on: boolean): void {
    this.editing = on;
    document.body.classList.toggle('hud-editing', on);
    $('layoutBar').hidden = !on;
    for (const u of UNITS) { const el = $(u.id); el.dataset.label = u.label; el.classList.toggle('hud-movable', on); if (u.resize) el.classList.toggle('hud-resizable', on); }
    if (!on) this.save();
    this.onEdit(on);
  }

  reset(): void { this.spots = {}; this.save(); this.apply(); }
  private save(): void { try { localStorage.setItem(KEY, JSON.stringify(this.spots)); } catch { /* not saved */ } }

  private draggable(id: string): void {
    const el = $(id);
    el.addEventListener('click', e => { if (this.editing) { e.preventDefault(); e.stopPropagation(); } }, true);   // no opening the world map while moving it
    el.addEventListener('pointerdown', e => {
      if (!this.editing) return;
      const r = el.getBoundingClientRect();
      // the bottom-right corner of the chat is for resizing, not moving
      if (UNITS.find(u => u.id === id)?.resize && e.clientX > r.right - 18 && e.clientY > r.bottom - 18) return;
      e.preventDefault(); e.stopPropagation();
      const dx = e.clientX - r.left, dy = e.clientY - r.top;
      el.setPointerCapture(e.pointerId);
      const move = (ev: PointerEvent) => {
        const x = Math.max(4, Math.min(innerWidth - r.width - 4, ev.clientX - dx)), y = Math.max(4, Math.min(innerHeight - 30, ev.clientY - dy));
        el.style.right = el.style.bottom = 'auto'; el.style.transform = 'none'; el.style.left = `${x}px`; el.style.top = `${y}px`;
        this.spots[id] = { ...this.spots[id], x: x / innerWidth, y: y / innerHeight };
      };
      const up = () => { el.removeEventListener('pointermove', move); el.removeEventListener('pointerup', up); el.classList.add('moved'); this.save(); };
      el.addEventListener('pointermove', move); el.addEventListener('pointerup', up);
    }, true);
    // the chat's width, when resized in edit mode
    if (UNITS.find(u => u.id === id)?.resize) new ResizeObserver(() => {
      if (!this.editing) return;
      const w = Math.round(el.getBoundingClientRect().width), r = el.getBoundingClientRect();
      this.spots[id] = { x: this.spots[id]?.x ?? r.left / innerWidth, y: this.spots[id]?.y ?? r.top / innerHeight, w };
    }).observe(el);
  }
}
